import axios from 'axios';
import { API_BASE, API_ORIGIN } from './apiConfig';

export { API_BASE, API_ORIGIN };

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

let activeApiRequestsCount = 0;

const notifyApiBusyState = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('app-api-busy', {
        detail: { active: activeApiRequestsCount > 0, count: activeApiRequestsCount },
      })
    );
  }
};

// Attach JWT token to requests, and handle FormData content type
api.interceptors.request.use((config) => {
  activeApiRequestsCount += 1;
  notifyApiBusyState();

  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let browser set Content-Type with boundary for FormData
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
}, (error) => {
  activeApiRequestsCount = Math.max(0, activeApiRequestsCount - 1);
  notifyApiBusyState();
  return Promise.reject(error);
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => {
    activeApiRequestsCount = Math.max(0, activeApiRequestsCount - 1);
    notifyApiBusyState();
    return response;
  },
  (error) => {
    activeApiRequestsCount = Math.max(0, activeApiRequestsCount - 1);
    notifyApiBusyState();

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('memberships');
      localStorage.removeItem('portalType');
      // Only redirect if on a protected page (not public endpoint paths)
      // Public endpoints include /schemes, /complaints (filing, not viewing), /content/*
      const isPublicView =
        error.config?.url?.includes('/schemes') ||
        error.config?.url?.includes('/content/') ||
        error.config?.url?.includes('/notices');

      if (!isPublicView && !window.location.pathname.includes('/login')) {
        window.location.href = window.location.pathname.startsWith('/system')
          ? '/system/login'
          : window.location.pathname.startsWith('/taluka')
            ? '/taluka/login'
            : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  requestOtp: (data) => api.post('/auth/otp/request', data),
  verifyOtp: (data) => api.post('/auth/otp/verify', data),
  requestRegistrationOtp: (data) => api.post('/auth/registration-otp/request', data),
  verifyRegistrationOtp: (data) => api.post('/auth/registration-otp/verify', data),
  requestForgotPasswordOtp: (data) => api.post('/auth/forgot-password/request-otp', data),
  verifyForgotPasswordOtp: (data) => api.post('/auth/forgot-password/verify-otp', data),
  resetPasswordWithToken: (data) => api.post('/auth/forgot-password/reset-password', data),
  register: (data) => {
    // For FormData (file uploads), use raw axios to avoid default Content-Type header
    if (data instanceof FormData) {
      return axios.post(`${API_BASE}/auth/register`, data);
    }
    return api.post('/auth/register', data);
  },
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  requestProfilePhoneOtp: (data) => api.post('/auth/profile/phone-otp/request', data),
  verifyProfilePhoneOtp: (data) => api.post('/auth/profile/phone-otp/verify', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  deleteAccount: (data) => api.post('/auth/delete-account', data),
  requestDataDeletionOtp: (data) => api.post('/auth/data-deletion/request-otp', data),
  submitDataDeletionRequest: (data) => api.post('/auth/data-deletion-request', data),
  getMemberships: () => api.get('/auth/memberships'),
  requestMembership: data => api.post('/auth/memberships/request', data),
  getMembershipRequests: () => api.get('/auth/memberships/requests'),
  withdrawMembershipRequest: (claimUid, data) => api.post(`/auth/memberships/requests/${claimUid}/withdraw`, data),
  selectMembership: (tenantSlug) => api.post('/auth/select-membership', { tenant_slug: tenantSlug }),
};

// Tenants
export const tenantAPI = {
  getAll: () => api.get('/tenants'),
  getBySlug: (slug) => api.get(`/tenants/${slug}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  updateContent: (id, data) => api.put(`/tenants/${id}/content`, data),
};

// Tenant-scoped API helper
const tenantUrl = (slug, path) => `/tenant/${slug}${path}`;

// Households
export const householdAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/households'), { params }),
  publicLookup: (slug, params) => api.get(tenantUrl(slug, '/households/public-lookup'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/households/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/households'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/households/${id}`), data),
  getDashboard: (slug) => api.get(tenantUrl(slug, '/households/dashboard')),
};

// Citizens
export const citizenAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/citizens'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/citizens/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/citizens'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/citizens/${id}`), data),
  getDemographics: (slug) => api.get(tenantUrl(slug, '/citizens/demographics')),
};

// Certificates
export const certificateAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/certificates'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/certificates/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/certificates'), data),
  updateStatus: (slug, id, data) => api.put(tenantUrl(slug, `/certificates/${id}/status`), data),
  updatePayment: (slug, id, data) => api.put(tenantUrl(slug, `/certificates/${id}/payment`), data),
  getDashboard: (slug) => api.get(tenantUrl(slug, '/certificates/dashboard')),
  uploadDoc: (slug, formData) => api.post(tenantUrl(slug, '/certificates/upload-doc'), formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  revokePendingUpload: (slug, uploadReceipt) => api.delete(tenantUrl(slug, '/certificates/upload-doc'), {
    headers: { 'X-Certificate-Upload-Receipt': uploadReceipt },
  }),
  getCertificateView: (slug, id) => api.get(tenantUrl(slug, `/certificates/${id}/view`)),
  downloadPdf: (slug, id) => api.get(tenantUrl(slug, `/certificates/${id}/pdf`), { responseType: 'blob' }),
  correctCertificateData: (slug, id, data) => api.patch(tenantUrl(slug, `/certificates/${id}/certificate-data`), data),
  reissue: (slug, id, data) => api.post(tenantUrl(slug, `/certificates/${id}/reissue`), data),
  getTemplates: (slug, params) => api.get(tenantUrl(slug, '/certificates/templates'), { params }),
  uploadTemplateAsset: (slug, formData) => api.post(tenantUrl(slug, '/certificates/templates/assets'), formData),
  createTemplateDraft: (slug, data) => api.post(tenantUrl(slug, '/certificates/templates'), data),
  updateTemplateDraft: (slug, templateId, data) => api.put(tenantUrl(slug, `/certificates/templates/${templateId}`), data),
  submitTemplate: (slug, templateId) => api.post(tenantUrl(slug, `/certificates/templates/${templateId}/submit`)),
  decideTemplate: (slug, templateId, data) => api.post(tenantUrl(slug, `/certificates/templates/${templateId}/decision`), data),
  lockTemplate: (slug, templateId, data) => api.post(tenantUrl(slug, `/certificates/templates/${templateId}/lock`), data),
  verify: (slug, certNumber) => api.get(tenantUrl(slug, `/certificates/verify/${certNumber}`)),
};

// Complaints
export const complaintAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/complaints'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/complaints/${id}`)),
  trackByUid: (slug, uid) => api.get(tenantUrl(slug, `/complaints/track/${uid}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/complaints'), data),
  updateStatus: (slug, id, data) => api.put(tenantUrl(slug, `/complaints/${id}/status`), data),
  addFeedback: (slug, id, data) => api.post(tenantUrl(slug, `/complaints/${id}/feedback`), data),
  getDashboard: (slug) => api.get(tenantUrl(slug, '/complaints/dashboard')),
};

// Schemes
export const schemeAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/schemes'), { params }),
  getAdminAll: (slug, params) => api.get(tenantUrl(slug, '/schemes/admin/list'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/schemes/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/schemes'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/schemes/${id}`), data),
  delete: (slug, id) => api.delete(tenantUrl(slug, `/schemes/${id}`)),
  getApplications: (slug, params) => api.get(tenantUrl(slug, '/schemes/applications/list'), { params }),
  apply: (slug, data) => api.post(tenantUrl(slug, '/schemes/applications/apply'), data),
  updateAppStatus: (slug, id, data) => api.put(tenantUrl(slug, `/schemes/applications/${id}/status`), data),
};

// Gramsabha
export const gramsabhaAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/gramsabha'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/gramsabha/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/gramsabha'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/gramsabha/${id}`), data),
  addResolution: (slug, id, data) => api.post(tenantUrl(slug, `/gramsabha/${id}/resolutions`), data),
  markAttendance: (slug, id, data) => api.post(tenantUrl(slug, `/gramsabha/${id}/attendance`), data),
  sendMom: (slug, id, data) => api.post(tenantUrl(slug, `/gramsabha/${id}/send-mom`), data),
};

// Finance
export const financeAPI = {
  // Categories
  getCategories: (slug) => api.get(tenantUrl(slug, '/finance/categories')),
  // Summary
  getSummary: (slug, params) => api.get(tenantUrl(slug, '/finance/summary'), { params }),
  // Budgets
  getBudgets: (slug, params) => api.get(tenantUrl(slug, '/finance/budgets'), { params }),
  getBudget: (slug, id) => api.get(tenantUrl(slug, `/finance/budgets/${id}`)),
  createBudget: (slug, data) => api.post(tenantUrl(slug, '/finance/budgets'), data),
  updateBudget: (slug, id, data) => api.put(tenantUrl(slug, `/finance/budgets/${id}`), data),
  approveBudget: (slug, id, data) => api.post(tenantUrl(slug, `/finance/budgets/${id}/approve`), data),
  deleteBudget: (slug, id) => api.delete(tenantUrl(slug, `/finance/budgets/${id}`)),
  // Transactions
  getTransactions: (slug, params) => api.get(tenantUrl(slug, '/finance/transactions'), { params }),
  getTransaction: (slug, id) => api.get(tenantUrl(slug, `/finance/transactions/${id}`)),
  createTransaction: (slug, data) => api.post(tenantUrl(slug, '/finance/transactions'), data),
  updateTransaction: (slug, id, data) => api.put(tenantUrl(slug, `/finance/transactions/${id}`), data),
  approveTransaction: (slug, id, data) => api.post(tenantUrl(slug, `/finance/transactions/${id}/approve`), data),
  reverseTransaction: (slug, id, data) => api.post(tenantUrl(slug, `/finance/transactions/${id}/reverse`), data),
  deleteTransaction: (slug, id) => api.delete(tenantUrl(slug, `/finance/transactions/${id}`)),
  exportTransactions: (slug, params) => api.get(tenantUrl(slug, '/finance/transactions/export'), { params, responseType: 'blob' }),
  exportReport: (slug, report, params) => api.get(tenantUrl(slug, `/finance/reports/${report}/export`), { params, responseType: 'blob' }),
  // Bills
  getBills: (slug, params) => api.get(tenantUrl(slug, '/finance/bills'), { params }),
  getBill: (slug, id) => api.get(tenantUrl(slug, `/finance/bills/${id}`)),
  createBill: (slug, data) => api.post(tenantUrl(slug, '/finance/bills'), data),
  updateBill: (slug, id, data) => api.put(tenantUrl(slug, `/finance/bills/${id}`), data),
  approveBill: (slug, id) => api.post(tenantUrl(slug, `/finance/bills/${id}/approve`)),
  deleteBill: (slug, id) => api.delete(tenantUrl(slug, `/finance/bills/${id}`)),
  payBill: (slug, id, data) => api.post(tenantUrl(slug, `/finance/bills/${id}/pay`), data),
  // Fund Allocations
  getFunds: (slug, params) => api.get(tenantUrl(slug, '/finance/funds'), { params }),
  getFund: (slug, id) => api.get(tenantUrl(slug, `/finance/funds/${id}`)),
  createFund: (slug, data) => api.post(tenantUrl(slug, '/finance/funds'), data),
  updateFund: (slug, id, data) => api.put(tenantUrl(slug, `/finance/funds/${id}`), data),
  deleteFund: (slug, id) => api.delete(tenantUrl(slug, `/finance/funds/${id}`)),
};

// Cross-registry maker/checker inbox. Decisions remain on the resource-specific
// workspace so reviewers see the complete immutable before/after snapshots.
export const registryChangeRequestAPI = {
  getBusinessQueue: (slug, params) => api.get(
    tenantUrl(slug, '/registry-change-requests/businesses'),
    { params }
  ),
  getPropertyQueue: (slug, params) => api.get(
    tenantUrl(slug, '/registry-change-requests/properties'),
    { params }
  ),
};

// Canonical Property Registry
export const propertyAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/properties'), { params }),
  checkMilkat: (slug, params) => api.get(tenantUrl(slug, '/properties/check-milkat'), { params }),
  getByUid: (slug, propertyUid) => api.get(tenantUrl(slug, `/properties/${propertyUid}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/properties'), data),
  syncHouseholds: (slug) => api.post(tenantUrl(slug, '/properties/sync-households')),
  update: (slug, propertyUid, data) => api.patch(tenantUrl(slug, `/properties/${propertyUid}`), data),
  submit: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/submit`), data),
  verify: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/verify`), data),
  reject: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/reject`), data),
  archive: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/archive`), data),
  replaceInterests: (slug, propertyUid, data) => api.put(tenantUrl(slug, `/properties/${propertyUid}/interests`), data),
  getInterestChanges: (slug, propertyUid, params) => api.get(
    tenantUrl(slug, `/registry-change-requests/properties/${propertyUid}/interests`),
    { params }
  ),
  proposeInterestChange: (slug, propertyUid, data) => api.post(
    tenantUrl(slug, `/registry-change-requests/properties/${propertyUid}/interests`),
    data
  ),
  approveInterestChange: (slug, propertyUid, requestUid, data) => api.post(
    tenantUrl(slug, `/registry-change-requests/properties/${propertyUid}/interests/${requestUid}/approve`),
    data
  ),
  rejectInterestChange: (slug, propertyUid, requestUid, data) => api.post(
    tenantUrl(slug, `/registry-change-requests/properties/${propertyUid}/interests/${requestUid}/reject`),
    data
  ),
  createOccupancy: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/occupancies`), data),
  updateOccupancy: (slug, propertyUid, occupancyId, data) => api.patch(tenantUrl(slug, `/properties/${propertyUid}/occupancies/${occupancyId}`), data),
  createValuation: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/valuations`), data),
  updateValuation: (slug, propertyUid, valuationUid, data) => api.patch(tenantUrl(slug, `/properties/${propertyUid}/valuations/${valuationUid}`), data),
  approveValuation: (slug, propertyUid, valuationUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/valuations/${valuationUid}/approve`), data),
  rejectValuation: (slug, propertyUid, valuationUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/valuations/${valuationUid}/reject`), data),
  createServiceConnection: (slug, propertyUid, data) => api.post(tenantUrl(slug, `/properties/${propertyUid}/service-connections`), data),
  updateServiceConnection: (slug, propertyUid, connectionUid, data) => api.patch(tenantUrl(slug, `/properties/${propertyUid}/service-connections/${connectionUid}`), data),
};

export const identityAPI = {
  searchParties: (slug, params) => api.get(tenantUrl(slug, '/identity/parties'), { params }),
  createParty: (slug, data) => api.post(tenantUrl(slug, '/identity/parties'), data),
  getMyIdentity: (slug) => api.get(tenantUrl(slug, '/identity/me')),
  getMyClaims: (slug, params) => api.get(tenantUrl(slug, '/identity/claims'), { params }),
  searchClaimTargets: (slug, params) => api.get(tenantUrl(slug, '/identity/claim-targets'), { params }),
  createClaim: (slug, data) => api.post(tenantUrl(slug, '/identity/claims'), data),
  withdrawClaim: (slug, claimUid, data) => api.post(tenantUrl(slug, `/identity/claims/${claimUid}/withdraw`), data),
  getMyIssues: (slug, params) => api.get(tenantUrl(slug, '/identity/issues'), { params }),
  getMyIssue: (slug, issueUid) => api.get(tenantUrl(slug, `/identity/issues/${issueUid}`)),
  createIssue: (slug, data) => api.post(tenantUrl(slug, '/identity/issues'), data),
  withdrawIssue: (slug, issueUid, data) => api.post(tenantUrl(slug, `/identity/issues/${issueUid}/withdraw`), data),
  getMyRegistryRequests: (slug, params) => api.get(tenantUrl(slug, '/identity/registry-requests'), { params }),
  createRegistryRequest: (slug, data) => api.post(tenantUrl(slug, '/identity/registry-requests'), data),
  withdrawRegistryRequest: (slug, requestUid, data) => api.post(tenantUrl(slug, `/identity/registry-requests/${requestUid}/withdraw`), data),
  getAdminIssues: (slug, params) => api.get(tenantUrl(slug, '/identity/admin/issues'), { params }),
  getAdminIssue: (slug, issueUid) => api.get(tenantUrl(slug, `/identity/admin/issues/${issueUid}`)),
  reviewIssue: (slug, issueUid, data) => api.post(tenantUrl(slug, `/identity/admin/issues/${issueUid}/review`), data),
  decideIssue: (slug, issueUid, data) => api.post(tenantUrl(slug, `/identity/admin/issues/${issueUid}/decide`), data),
  getAdminRegistryRequests: (slug, params) => api.get(tenantUrl(slug, '/identity/admin/registry-requests'), { params }),
  reviewRegistryRequest: (slug, requestUid, data) => api.post(tenantUrl(slug, `/identity/admin/registry-requests/${requestUid}/review`), data),
  decideRegistryRequest: (slug, requestUid, data) => api.post(tenantUrl(slug, `/identity/admin/registry-requests/${requestUid}/decide`), data),
  getAdminClaims: (slug, params) => api.get(tenantUrl(slug, '/identity/admin/claims'), { params }),
  approveAdminClaim: (slug, claimUid, data) => api.put(tenantUrl(slug, `/identity/admin/claims/${claimUid}/approve`), data),
  rejectAdminClaim: (slug, claimUid, data) => api.put(tenantUrl(slug, `/identity/admin/claims/${claimUid}/reject`), data),
};

// Tenant-scoped, append-only audit history. Export is deliberately exposed as
// a separate method because the backend enforces its own `audit_log.export`
// permission in addition to the normal view permission.
export const auditAPI = {
  getEvents: (slug, params) => api.get(tenantUrl(slug, '/audit/events'), { params }),
  exportEvents: (slug, params) => api.get(tenantUrl(slug, '/audit/events.csv'), {
    params,
    responseType: 'blob',
  }),
};

// Versioned tax engine. This intentionally remains separate from taxAPI while
// legacy assessments are still available during migration.
const taxEngineUrl = (slug, path) => tenantUrl(slug, `/tax-engine${path}`);
const idempotency = key => ({ headers: { 'Idempotency-Key': key, 'X-Idempotency-Key': key } });

export const taxEngineAPI = {
  // Taxpayer account, demand, case, payment and receipt views.
  getMyAccounts: (slug, params) => api.get(taxEngineUrl(slug, '/my/accounts'), { params }),
  getMyAccount: (slug, accountUid) => api.get(taxEngineUrl(slug, `/my/accounts/${accountUid}`)),
  downloadMyAccountDemandNotice: (slug, accountUid, params) => api.get(taxEngineUrl(slug, `/my/accounts/${accountUid}/demand-notice`), { params, responseType: 'blob' }),
  getMyAccountDemandNotice: (slug, accountUid, params) => api.get(taxEngineUrl(slug, `/my/accounts/${accountUid}/demand-notice-data`), { params }),
  getMyDemands: (slug, params) => api.get(taxEngineUrl(slug, '/my/demands'), { params }),
  getMyDemand: (slug, demandUid) => api.get(taxEngineUrl(slug, `/my/demands/${demandUid}`)),
  downloadMyDemandNotice: (slug, demandUid) => api.get(taxEngineUrl(slug, `/my/demands/${demandUid}/demand-notice`), { responseType: 'blob' }),
  getMyReceipts: (slug, params) => api.get(taxEngineUrl(slug, '/my/receipts'), { params }),
  getMyReceipt: (slug, receiptUid) => api.get(taxEngineUrl(slug, `/my/receipts/${receiptUid}`)),
  downloadMyReceipt: (slug, receiptUid) => api.get(taxEngineUrl(slug, `/my/receipts/${receiptUid}/download-data`), { responseType: 'blob' }),
  createPaymentOrder: (slug, data, key) => api.post(taxEngineUrl(slug, '/payments/orders'), data, idempotency(key)),
  getPaymentAvailability: (slug) => api.get(taxEngineUrl(slug, '/payments/availability')),
  verifyPayment: (slug, data) => api.post(taxEngineUrl(slug, '/payments/verify'), data),
  getPaymentIntent: (slug, intentUid) => api.get(taxEngineUrl(slug, `/payments/intents/${intentUid}`)),
  getMyExemptions: (slug, params) => api.get(taxEngineUrl(slug, '/my/exemptions'), { params }),
  requestExemption: (slug, accountUid, data) => api.post(taxEngineUrl(slug, `/my/accounts/${accountUid}/exemption-requests`), data),
  withdrawMyExemption: (slug, exemptionUid, data) => api.post(taxEngineUrl(slug, `/my/exemptions/${exemptionUid}/withdraw`), data),
  getMyAppeals: (slug, params) => api.get(taxEngineUrl(slug, '/my/appeals'), { params }),
  createAppeal: (slug, demandUid, data) => api.post(taxEngineUrl(slug, `/my/demands/${demandUid}/appeals`), data),
  withdrawMyAppeal: (slug, appealUid, data) => api.post(taxEngineUrl(slug, `/my/appeals/${appealUid}/withdraw`), data),

  // Administration and maker-checker configuration.
  getAccounts: (slug, params) => api.get(taxEngineUrl(slug, '/accounts'), { params }),
  getAccount: (slug, accountUid) => api.get(taxEngineUrl(slug, `/accounts/${accountUid}`)),
  downloadAccountDemandNotice: (slug, accountUid, params) => api.get(taxEngineUrl(slug, `/accounts/${accountUid}/demand-notice`), { params, responseType: 'blob' }),
  getAccountDemandNotice: (slug, accountUid, params) => api.get(taxEngineUrl(slug, `/accounts/${accountUid}/demand-notice-data`), { params }),
  createAccount: (slug, data) => api.post(taxEngineUrl(slug, '/accounts'), data),
  provisionAllAccounts: (slug, data, key) => api.post(taxEngineUrl(slug, '/accounts/provision-all'), data, idempotency(key)),
  updateAccount: (slug, accountUid, data) => api.patch(taxEngineUrl(slug, `/accounts/${accountUid}`), data),
  activateAccount: (slug, accountUid, data) => api.post(taxEngineUrl(slug, `/accounts/${accountUid}/activate`), data),
  closeAccount: (slug, accountUid, data) => api.post(taxEngineUrl(slug, `/accounts/${accountUid}/close`), data),
  sendAccountTaxReminder: (slug, accountUid) => api.post(taxEngineUrl(slug, `/accounts/${accountUid}/send-reminder`)),
  getRules: (slug, params) => api.get(taxEngineUrl(slug, '/rules'), { params }),
  getRule: (slug, ruleUid) => api.get(taxEngineUrl(slug, `/rules/${ruleUid}`)),
  createRule: (slug, data) => api.post(taxEngineUrl(slug, '/rules'), data),
  updateRule: (slug, ruleUid, data) => api.patch(taxEngineUrl(slug, `/rules/${ruleUid}`), data),
  createRuleVersion: (slug, ruleUid, data) => api.post(taxEngineUrl(slug, `/rules/${ruleUid}/versions`), data),
  updateRuleVersion: (slug, versionUid, data) => api.patch(taxEngineUrl(slug, `/rule-versions/${versionUid}`), data),
  approveRuleVersion: (slug, versionUid, data) => api.post(taxEngineUrl(slug, `/rule-versions/${versionUid}/approve`), data),
  approveRuleVersionSuccessor: (slug, versionUid, data) => api.post(taxEngineUrl(slug, `/rule-versions/${versionUid}/approve-successor`), data),
  getAssessmentRuns: (slug, params) => api.get(taxEngineUrl(slug, '/assessment-runs'), { params }),
  getAssessmentRun: (slug, runUid) => api.get(taxEngineUrl(slug, `/assessment-runs/${runUid}`)),
  previewAssessment: (slug, data, key) => api.post(taxEngineUrl(slug, '/assessment-runs/preview'), data, idempotency(key)),
  previewAnnualAssessment: (slug, data, key) => api.post(taxEngineUrl(slug, '/assessment-runs/annual-preview'), data, idempotency(key)),
  generateAnnualAssessment: (slug, data, key) => api.post(taxEngineUrl(slug, '/assessment-runs/annual-generate'), data, idempotency(key)),
  approveAssessmentRun: (slug, runUid, data) => api.post(taxEngineUrl(slug, `/assessment-runs/${runUid}/approve`), data),
  issueAssessmentRun: (slug, runUid, data) => api.post(taxEngineUrl(slug, `/assessment-runs/${runUid}/issue`), data),
  getDemands: (slug, params) => api.get(taxEngineUrl(slug, '/demands'), { params }),
  getDemand: (slug, demandUid) => api.get(taxEngineUrl(slug, `/demands/${demandUid}`)),
  downloadDemandNotice: (slug, demandUid) => api.get(taxEngineUrl(slug, `/demands/${demandUid}/demand-notice`), { responseType: 'blob' }),
  cancelDemand: (slug, demandUid, data) => api.post(taxEngineUrl(slug, `/demands/${demandUid}/cancel`), data),
  createAdjustment: (slug, demandUid, data) => api.post(taxEngineUrl(slug, `/demands/${demandUid}/adjustments`), data),
  approveAdjustment: (slug, adjustmentUid, data) => api.post(taxEngineUrl(slug, `/adjustments/${adjustmentUid}/approve`), data),

  // Collections and finance reconciliation.
  searchCollectionPayers: (slug, params) => api.get(taxEngineUrl(slug, '/collections/payers'), { params }),
  createManualCollection: (slug, data, key) => api.post(taxEngineUrl(slug, '/collections/manual'), data, idempotency(key)),
  getReceipts: (slug, params) => api.get(taxEngineUrl(slug, '/collections/receipts'), { params }),
  getReceipt: (slug, receiptUid) => api.get(taxEngineUrl(slug, `/collections/receipts/${receiptUid}`)),
  downloadReceipt: (slug, receiptUid) => api.get(taxEngineUrl(slug, `/collections/receipts/${receiptUid}/download-data`), { responseType: 'blob' }),
  createReceiptAction: (slug, receiptUid, data, key) => api.post(taxEngineUrl(slug, `/collections/receipts/${receiptUid}/actions`), data, idempotency(key)),
  getReconciliation: (slug) => api.get(taxEngineUrl(slug, '/reconciliation')),
  resolvePaymentIntent: (slug, intentUid, data) => api.post(taxEngineUrl(slug, `/reconciliation/intents/${intentUid}/resolve`), data),
  resolveManualCollection: (slug, attemptUid, data, key) => api.post(taxEngineUrl(slug, `/reconciliation/manual-collections/${attemptUid}/resolve`), data, idempotency(key)),
  resolveReceiptAction: (slug, actionUid, data) => api.post(taxEngineUrl(slug, `/reconciliation/receipt-actions/${actionUid}/resolve`), data),

  // Exemptions and appeal casework.
  getExemptions: (slug, params) => api.get(taxEngineUrl(slug, '/exemptions'), { params }),
  createExemption: (slug, accountUid, data) => api.post(taxEngineUrl(slug, `/accounts/${accountUid}/exemptions`), data),
  updateExemption: (slug, exemptionUid, data) => api.patch(taxEngineUrl(slug, `/exemptions/${exemptionUid}`), data),
  decideExemption: (slug, exemptionUid, decision, data) => api.post(taxEngineUrl(slug, `/exemptions/${exemptionUid}/${decision}`), data),
  expireExemptions: (slug) => api.post(taxEngineUrl(slug, '/exemptions/maintenance/expire')),
  getAppeals: (slug, params) => api.get(taxEngineUrl(slug, '/appeals'), { params }),
  getAppeal: (slug, appealUid) => api.get(taxEngineUrl(slug, `/appeals/${appealUid}`)),
  progressAppeal: (slug, appealUid, data) => api.post(taxEngineUrl(slug, `/appeals/${appealUid}/progress`), data),
  decideAppeal: (slug, appealUid, data) => api.post(taxEngineUrl(slug, `/appeals/${appealUid}/decide`), data),
  getReportSummary: (slug, params) => api.get(taxEngineUrl(slug, '/reports/summary'), { params }),
  exportDemandReport: (slug, params) => api.get(taxEngineUrl(slug, '/reports/demands.csv'), { params, responseType: 'blob' }),
  downloadImportTemplate: (slug) => api.get(taxEngineUrl(slug, '/import-template'), { responseType: 'blob' }),
  importHouseholdTaxExcel: (slug, formData, dryRun = false) => api.post(
    taxEngineUrl(slug, `/import-excel${dryRun ? '?dry_run=true' : ''}`),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ),
};

// Canonical Business Registry. Public directory reads use getPublic/getPublicById;
// all private compliance and identity fields stay behind the authenticated admin
// and membership-bound owner routes below.
export const businessAPI = {
  getPublic: (slug, params) => api.get(tenantUrl(slug, '/businesses'), { params }),
  getPublicById: (slug, businessId) => api.get(tenantUrl(slug, `/businesses/${businessId}`)),
  getAdmin: (slug, params) => api.get(tenantUrl(slug, '/businesses/admin'), { params }),
  getAdminById: (slug, businessId) => api.get(tenantUrl(slug, `/businesses/admin/${businessId}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/businesses'), data),
  update: (slug, businessId, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}`), data),
  submit: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/submit`), data),
  verify: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/verify`), data),
  approveBusiness: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/approve`), data),
  reject: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/reject`), data),
  archive: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/archive`), data),
  addPartyRole: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/party-roles`), data),
  replacePartyRoles: (slug, businessId, data) => api.put(tenantUrl(slug, `/businesses/${businessId}/party-roles`), data),
  updatePartyRole: (slug, businessId, roleId, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}/party-roles/${roleId}`), data),
  endPartyRole: (slug, businessId, roleId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/party-roles/${roleId}/end`), data),
  getPartyRoleChangeRequests: (slug, businessId, params) => api.get(tenantUrl(slug, `/registry-change-requests/businesses/${businessId}/party-roles`), { params }),
  proposePartyRoleChange: (slug, businessId, data) => api.post(tenantUrl(slug, `/registry-change-requests/businesses/${businessId}/party-roles`), data),
  approvePartyRoleChange: (slug, businessId, requestUid, data) => api.post(tenantUrl(slug, `/registry-change-requests/businesses/${businessId}/party-roles/${requestUid}/approve`), data),
  rejectPartyRoleChange: (slug, businessId, requestUid, data) => api.post(tenantUrl(slug, `/registry-change-requests/businesses/${businessId}/party-roles/${requestUid}/reject`), data),
  addLocation: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/locations`), data),
  updateLocation: (slug, businessId, locationId, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}/locations/${locationId}`), data),
  closeLocation: (slug, businessId, locationId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/locations/${locationId}/close`), data),
  getAccessGrants: (slug, businessId) => api.get(tenantUrl(slug, `/businesses/${businessId}/access-grants`)),
  createAccessGrant: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/access-grants`), data),
  revokeAccessGrant: (slug, businessId, grantUid, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/access-grants/${grantUid}/revoke`), data),
  getTaxDeclarations: (slug, businessId, params) => api.get(tenantUrl(slug, `/businesses/${businessId}/tax-declarations`), { params }),
  createTaxDeclaration: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/tax-declarations`), data),
  updateTaxDeclaration: (slug, businessId, declarationUid, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}/tax-declarations/${declarationUid}`), data),
  submitTaxDeclaration: (slug, businessId, declarationUid, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/tax-declarations/${declarationUid}/submit`), data),
  approveTaxDeclaration: (slug, businessId, declarationUid, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/tax-declarations/${declarationUid}/approve`), data),
  rejectTaxDeclaration: (slug, businessId, declarationUid, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/tax-declarations/${declarationUid}/reject`), data),
  getMine: (slug) => api.get(tenantUrl(slug, '/businesses/mine')),
  getMineById: (slug, businessId) => api.get(tenantUrl(slug, `/businesses/${businessId}/self`)),
  updateMine: (slug, businessId, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}/self`), data),
  addMyLocation: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/self/locations`), data),
  updateMyLocation: (slug, businessId, locationId, data) => api.patch(tenantUrl(slug, `/businesses/${businessId}/self/locations/${locationId}`), data),
  closeMyLocation: (slug, businessId, locationId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/self/locations/${locationId}/close`), data),
  getMyTaxDeclarations: (slug, businessId, params) => api.get(tenantUrl(slug, `/businesses/${businessId}/self/tax-declarations`), { params }),
  submitMyTaxDeclaration: (slug, businessId, data) => api.post(tenantUrl(slug, `/businesses/${businessId}/self/tax-declarations/submit`), data),
};

// Business licences, NOCs, registrations, renewals and inspections. The
// self-service methods are still protected by membership-bound business
// grants on the server; keeping them separate from the staff catalogue and
// review methods makes accidental privilege mixing harder in callers.
export const businessComplianceAPI = {
  getReferenceData: (slug) => api.get(tenantUrl(slug, '/business-compliance/reference-data')),

  getRequirements: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/requirements'), { params }),
  createRequirement: (slug, data) => api.post(tenantUrl(slug, '/business-compliance/requirements'), data),
  updateRequirement: (slug, requirementUid, data) => api.patch(tenantUrl(slug, `/business-compliance/requirements/${requirementUid}`), data),
  archiveRequirement: (slug, requirementUid, data) => api.post(tenantUrl(slug, `/business-compliance/requirements/${requirementUid}/archive`), data),

  getCases: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/cases'), { params }),
  getCase: (slug, caseUid) => api.get(tenantUrl(slug, `/business-compliance/cases/${caseUid}`)),
  uploadCaseDocument: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/cases/${caseUid}/documents`), data),
  downloadCaseDocument: (slug, caseUid, documentId) => api.get(tenantUrl(slug, `/business-compliance/cases/${caseUid}/documents/${documentId}`), { responseType: 'blob' }),
  assignCase: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/cases/${caseUid}/assign`), data),
  linkFeeDemand: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/cases/${caseUid}/link-fee-demand`), data),
  transitionCase: (slug, caseUid, command, data) => api.post(tenantUrl(slug, `/business-compliance/cases/${caseUid}/${command}`), data),
  scheduleInspection: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/cases/${caseUid}/inspections`), data),
  updateInspection: (slug, caseUid, inspectionUid, data) => api.patch(tenantUrl(slug, `/business-compliance/cases/${caseUid}/inspections/${inspectionUid}`), data),
  expireDueCases: (slug, data) => api.post(tenantUrl(slug, '/business-compliance/maintenance/expire-due'), data),
  getSummary: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/reports/summary'), { params }),
  exportCases: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/reports/export.csv'), { params, responseType: 'blob' }),

  getMyRequirements: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/self/requirements'), { params }),
  getMyCases: (slug, params) => api.get(tenantUrl(slug, '/business-compliance/self/cases'), { params }),
  uploadMyDocument: (slug, businessId, data) => api.post(tenantUrl(slug, `/business-compliance/self/businesses/${businessId}/documents`), data),
  createMyCase: (slug, businessId, data) => api.post(tenantUrl(slug, `/business-compliance/self/businesses/${businessId}/cases`), data),
  getMyCase: (slug, caseUid) => api.get(tenantUrl(slug, `/business-compliance/self/cases/${caseUid}`)),
  downloadMyDocument: (slug, caseUid, documentId) => api.get(tenantUrl(slug, `/business-compliance/self/cases/${caseUid}/documents/${documentId}`), { responseType: 'blob' }),
  updateMyCase: (slug, caseUid, data) => api.patch(tenantUrl(slug, `/business-compliance/self/cases/${caseUid}`), data),
  submitMyCase: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/self/cases/${caseUid}/submit`), data),
  withdrawMyCase: (slug, caseUid, data) => api.post(tenantUrl(slug, `/business-compliance/self/cases/${caseUid}/withdraw`), data),
};

// Content (Public + Admin)
export const contentAPI = {
  getPublicData: (slug) => api.get(tenantUrl(slug, '/content/public')),
  getPages: (slug) => api.get(tenantUrl(slug, '/content/pages')),
  getPagesAdmin: (slug) => api.get(tenantUrl(slug, '/content/pages-admin')),
  getPage: (slug, pageSlug) => api.get(tenantUrl(slug, `/content/pages/${pageSlug}`)),
  createPage: (slug, data) => api.post(tenantUrl(slug, '/content/pages'), data),
  updatePage: (slug, id, data) => api.put(tenantUrl(slug, `/content/pages/${id}`), data),
  deletePage: (slug, id) => api.delete(tenantUrl(slug, `/content/pages/${id}`)),
  getPublicNotices: (slug) => api.get(tenantUrl(slug, '/content/notices')),
  getNotices: (slug) => api.get(tenantUrl(slug, '/content/notices-admin')),
  createNotice: (slug, data) => api.post(tenantUrl(slug, '/content/notices'), data),
  updateNotice: (slug, id, data) => api.put(tenantUrl(slug, `/content/notices/${id}`), data),
  deleteNotice: (slug, id) => api.delete(tenantUrl(slug, `/content/notices/${id}`)),
  getGallery: (slug) => api.get(tenantUrl(slug, '/content/gallery')),
  getGalleryAdmin: (slug) => api.get(tenantUrl(slug, '/content/gallery-admin')),
  addGalleryImage: (slug, data) => api.post(tenantUrl(slug, '/content/gallery'), data),
  updateGalleryMedia: (slug, id, data) => api.put(tenantUrl(slug, `/content/gallery/${id}`), data),
  deleteGalleryImage: (slug, id) => api.delete(tenantUrl(slug, `/content/gallery/${id}`)),
  getFees: (slug) => api.get(tenantUrl(slug, '/content/fees')),
  getFeesAdmin: (slug) => api.get(tenantUrl(slug, '/content/fees-admin')),
  upsertFee: (slug, data) => api.post(tenantUrl(slug, '/content/fees'), data),
  uploadImage: (slug, formData) => api.post(tenantUrl(slug, '/content/upload-image'), formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadFile: (slug, formData) => api.post(tenantUrl(slug, '/content/upload-file'), formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadGalleryMedia: (slug, formData) => api.post(tenantUrl(slug, '/content/upload-media'), formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Projects
export const projectAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/projects'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/projects/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/projects'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/projects/${id}`), data),
  delete: (slug, id) => api.delete(tenantUrl(slug, `/projects/${id}`)),
};

export const contactMessageAPI = {
  submit: (slug, data) => api.post(tenantUrl(slug, '/contact-messages'), data),
  getAll: (slug, params) => api.get(tenantUrl(slug, '/contact-messages'), { params }),
  updateStatus: (slug, messageUid, status) => api.patch(
    tenantUrl(slug, `/contact-messages/${messageUid}/status`),
    { status }
  ),
};

// Departments
export const departmentAPI = {
  getAll: (slug) => api.get(tenantUrl(slug, '/departments')),
  getById: (slug, id) => api.get(tenantUrl(slug, `/departments/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/departments'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/departments/${id}`), data),
  delete: (slug, id) => api.delete(tenantUrl(slug, `/departments/${id}`)),
};

// Facilities
export const facilityAPI = {
  getAll: (slug) => api.get(tenantUrl(slug, '/facilities')),
  getById: (slug, id) => api.get(tenantUrl(slug, `/facilities/${id}`)),
  create: (slug, data) => api.post(tenantUrl(slug, '/facilities'), data),
  update: (slug, id, data) => api.put(tenantUrl(slug, `/facilities/${id}`), data),
  delete: (slug, id) => api.delete(tenantUrl(slug, `/facilities/${id}`)),
};

// User Approvals (Admin)
export const userApprovalAPI = {
  getAll: (slug, params) => api.get(tenantUrl(slug, '/user-approvals'), { params }),
  getById: (slug, id) => api.get(tenantUrl(slug, `/user-approvals/${id}`)),
  approve: (slug, id, data) => api.put(tenantUrl(slug, `/user-approvals/${id}/approve`), data),
  reject: (slug, id, data) => api.put(tenantUrl(slug, `/user-approvals/${id}/reject`), data),
  getStats: (slug) => api.get(tenantUrl(slug, '/user-approvals/stats')),
};

// Taluka Officer API (cross-GP queries)
export const talukaAPI = {
  getDashboard: () => api.get('/taluka/dashboard'),
  getGrampanchayats: () => api.get('/taluka/grampanchayats'),
  getComplaints: (params) => api.get('/taluka/complaints', { params }),
  getCitizens: (params) => api.get('/taluka/citizens', { params }),
  getStaff: (params) => api.get('/taluka/staff', { params }),
  getNotices: (params) => api.get('/taluka/notices', { params }),
  getFacilities: (params) => api.get('/taluka/facilities', { params }),
  getBusinesses: (params) => api.get('/taluka/businesses', { params }),
  getPopulation: () => api.get('/taluka/population'),
};

// System Admin API (platform management)
export const systemAPI = {
  getDashboard: () => api.get('/system/dashboard'),
  getTenants: (params) => api.get('/system/tenants', { params }),
  getTenantById: (id) => api.get(`/system/tenants/${id}`),
  createTenant: (data) => api.post('/system/tenants', data),
  updateTenant: (id, data) => api.put(`/system/tenants/${id}`, data),
  deactivateTenant: (id) => api.patch(`/system/tenants/${id}/deactivate`),
  activateTenant: (id) => api.patch(`/system/tenants/${id}/activate`),
  getUsers: (params) => api.get('/system/users', { params }),
  createUser: (data) => api.post('/system/users', data),
  updateUser: (id, data) => api.put(`/system/users/${id}`, data),
  resetUserPassword: (id, data) => api.put(`/system/users/${id}/reset-password`, data),
  updateUserPermissions: (id, data) => api.put(`/system/users/${id}/permissions`, data),
  getLegacyTaxMigrationStatus: (tenantId) => api.get(`/system/legacy-tax-migrations/${tenantId}/status`),
  runLegacyTaxDryRun: (tenantId) => api.post(`/system/legacy-tax-migrations/${tenantId}/dry-run`, {}),
  applyLegacyTaxMigration: (tenantId, data) => api.post(`/system/legacy-tax-migrations/${tenantId}/apply`, data),
  cutoverLegacyTaxMigration: (tenantId, data) => api.post(`/system/legacy-tax-migrations/${tenantId}/cutover`, data),
  downloadLegacyTaxMigrationReport: (tenantId, runId) => api.get(`/system/legacy-tax-migrations/${tenantId}/runs/${runId}/report`, { responseType: 'blob' }),
};

// RBAC API (role & permission management)
export const rbacAPI = {
  getPermissionModules: () => api.get('/system/rbac/permissions'),
  getRoles: (params) => api.get('/system/rbac/roles', { params }),
  getRoleById: (id) => api.get(`/system/rbac/roles/${id}`),
  createRole: (data) => api.post('/system/rbac/roles', data),
  updateRole: (id, data) => api.put(`/system/rbac/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/system/rbac/roles/${id}`),
  applyDefaults: (id) => api.post(`/system/rbac/roles/${id}/apply-defaults`),
};

export default api;
